import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { QuestionModel } from '../../features/surveys/models/question.model';
import { CmdModel } from '../../features/surveys/models/cmd.model';
import { UserStoreService } from './user-store.service';

export interface ApiSurveyDto {
  id: string;
  name: string;
  questions: Record<string, QuestionModel>;
  cmd: CmdModel[];
  businessRules: BusinessRulesModel[];
  fieldwork: FieldworkModel;
}

export interface ApiSurveyPatchResponse {
  applied: string[];
  failed: {code:string, operation: string, reason: string}[];
  isFullSuccess: boolean
}

export interface BusinessRulesModel {
  enabled: boolean;
  name: string;
  type: string;
  parameters: Record<string, string>;
  values: string[],
  field: string | null,
  priority: 0 | 1,
  segmentScope?: {
    accept?: string[] | null;
    reject?: string[] | null;
  } | null;
}

export interface FieldworkModel {
    delay: Record<string, {date:string, days:number }>;
    schedule: {
      weekDays: string;
      rules: Record<number, { channels: DispatchChannels[], smsProvider:string }>;
      ruleOverride: FieldworkRuleOverrideModel[];
    }
}

export interface FieldworkRuleOverrideModel {
  element: string;
  value: string;
  segments: string[];
}

export type DispatchChannels = "email" | "sms" | "whatsapp";

@Injectable({ providedIn: 'root' })
export class ApiSurveyModelService {
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly apiPath = '/surveymodel';

  constructor(
    private http: HttpClient,
    private userStore: UserStoreService) {}

  getSurveyModel(organizationId: string, surveyId: string): Observable<ApiSurveyDto> {
    const params = new HttpParams()
      .set('organization', organizationId)
      .set('survey', surveyId);

    return this.http.get<ApiSurveyDto>(`${this.apiBaseUrl}${this.apiPath}`, {params})
  }

  patchSurveyModel(organizationId: string, surveyId: string, surveyModel: Record<string, QuestionModel>): Observable<ApiSurveyPatchResponse> {
    //Prevent update of other surveys during development, unless the user is a SuperAdmin
    if(organizationId != 'a535f93d-f8c7-421d-a9c9-74f7a17e3761' && !this.userStore.isSuperAdmin()){
      return throwError(() => new Error('This organization/survey is not editable.'));
    }

    const request = {
      organization: organizationId,
      survey: surveyId,
      createOrUpdate: surveyModel
    }
    
    return this.http.post<ApiSurveyPatchResponse>(`${this.apiBaseUrl}${this.apiPath}`, request)
  }

  deleteSurveyModel(organizationId: string, surveyId: string, qCodes: string[]): Observable<ApiSurveyPatchResponse> {
    //Prevent update of other surveys during development, unless the user is a SuperAdmin
    if(organizationId != 'a535f93d-f8c7-421d-a9c9-74f7a17e3761' && !this.userStore.isSuperAdmin()){
      return throwError(() => new Error('This organization/survey is not editable.'));
    }

    const request = {
      organization: organizationId,
      survey: surveyId,
      delete: qCodes
    }
    
    return this.http.post<ApiSurveyPatchResponse>(`${this.apiBaseUrl}${this.apiPath}`, request)
  }
}